import { API, BACKEND_URL } from '@/config/api';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { isDemoUser } from '@/lib/utils';
import AdminNavigation from '@/components/AdminNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Building2, Plus, Edit2, Trash2, Users, MapPin, Mail, Phone, Loader2, Search, User } from 'lucide-react';

export default function AdminCompanies() {
  const { user } = useAuth();
  const isDemo = isDemoUser(user);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: ''
  });

  const fetchCompanies = useCallback(async () => {
    // Real API only
    try {
      const response = await axios.get(`${API}/companies`, { withCredentials: true });
      setCompanies(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Feil ved henting av kunder:', error);
      toast.error('Kunne ikke laste kunder');
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    // Only fetch if not demo user
    if (isDemo) {
      setCompanies([
        {
          id: 'company-mowi',
          name: 'Mowi ASA',
          contact_person: 'Kari Nordmann',
          email: 'kontakt@mowi.no',
          phone: '+47 12 34 56 78'
        },
        {
          id: 'company-leroy', 
          name: 'Lerøy Seafood',
          contact_person: 'Ola Hansen',
          email: 'post@leroy.no',
          phone: '+47 98 76 54 32'
        },
        {
          id: 'company-salmar',
          name: 'SalMar ASA', 
          contact_person: 'Nina Berg',
          email: 'kontakt@salmar.no',
          phone: '+47 11 22 33 44'
        }
      ]);
      setLoading(false);
      return;
    }
    fetchCompanies();
  }, [isDemo, fetchCompanies]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Demo mode - simulate success
    if (isDemo) {
      toast.success('Demo kunde opprettet!');
      setDialogOpen(false);
      setFormData({ name: '', contact_person: '', email: '', phone: '' });
      // Add to demo companies list
      const newCompany = { ...formData, id: Date.now().toString() };
      setCompanies(prev => [...prev, newCompany]);
      return;
    }
    
    // Real API call
    try {
      await axios.post(
        `${API}/companies`,
        formData,
        { withCredentials: true }
      );
      toast.success('Kunde opprettet');
      setDialogOpen(false);
      setFormData({ name: '', contact_person: '', email: '', phone: '' });
      fetchCompanies();
    } catch (error) {
      console.error('Feil ved opprettelse:', error);
      toast.error('Kunne ikke opprette kunde');
    }
  };
  
  if (loading) {
    return (
      <>
        <AdminNavigation />
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-blue-50">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Laster kunder...</p>
          </div>
        </div>
      </>
    );
  }
  
  return (
    <>
      <AdminNavigation />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mb-2">Kunder</h1>
              <p className="text-base text-slate-600">Administrer kundebedrifter</p>
            </div>
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-company-btn" className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-5 h-5 mr-2" />
                  Ny kunde
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Legg til ny kunde</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Bedriftsnavn *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="F.eks. Mowi ASA"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_person">Kontaktperson</Label>
                    <Input
                      id="contact_person"
                      value={formData.contact_person}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      placeholder="Navn på kontaktperson"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">E-post</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="kontakt@bedrift.no"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Telefon</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+47 123 45 678"
                    />
                  </div>
                  <div className="flex space-x-2 pt-4">
                    <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700">
                      Opprett
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      className="flex-1"
                    >
                      Avbryt
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          
          {companies.length === 0 ? (
            <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
              <CardContent className="p-12 text-center">
                <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-2">Ingen kunder registrert</p>
                <p className="text-sm text-slate-500">Legg til din første kunde for å komme i gang</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {companies.map((company) => (
                <Card key={company.id} data-testid={`company-card-${company.id}`} className="border-0 shadow-lg bg-white/80 backdrop-blur-sm hover:shadow-xl transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-slate-800 mb-3">{company.name}</h3>
                        
                        <div className="space-y-2 text-sm">
                          {company.contact_person && (
                            <div className="flex items-center text-slate-600">
                              <User className="w-4 h-4 mr-2 flex-shrink-0" />
                              <span className="truncate">{company.contact_person}</span>
                            </div>
                          )}
                          {company.email && (
                            <div className="flex items-center text-slate-600">
                              <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                              <span className="truncate">{company.email}</span>
                            </div>
                          )}
                          {company.phone && (
                            <div className="flex items-center text-slate-600">
                              <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                              <span className="truncate">{company.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}